import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// ── Shared styles from HTML ──────────────────────────────────────────────────
const S = {
    section: {
        padding: '96px 24px',
        maxWidth: 1140,
        margin: '0 auto'
    },
    chip: {
        display: 'inline-block',
        padding: '6px 16px',
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'Nunito, sans-serif',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        marginBottom: 16
    },
    h2: {
        fontFamily: 'Nunito, sans-serif',
        fontWeight: 900,
        fontSize: 'clamp(32px, 5vw, 52px)',
        lineHeight: 1.1,
        marginBottom: 16,
        textWrap: 'pretty'
    },
    lead: {
        fontSize: 19,
        lineHeight: 1.65,
        color: 'oklch(0.50 0.02 75)',
        maxWidth: 600,
        textWrap: 'pretty'
    },
    card: {
        background: '#fff',
        borderRadius: 24,
        padding: 32,
        boxShadow: '0 2px 24px oklch(0.22 0.025 75 / 0.07)'
    }
};

const modalInputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: 12,
    border: '2px solid oklch(0.88 0.02 75)',
    fontFamily: 'Nunito Sans, sans-serif', fontSize: 15, color: 'oklch(0.22 0.025 75)',
    background: 'oklch(0.985 0.008 75)', outline: 'none', transition: 'border-color 0.2s'
};

// ── Login Modal Component ──────────────────────────────────────────────────
function LoginModal({ onClose, onSignWithGoogle, initialTab = 'login' }) {
    const [error, setError] = useState('');
    const [googleLoading, setGoogleLoading] = useState(false);

    const handleGoogle = async () => {
        setError('');
        setGoogleLoading(true);
        try {
            await onSignWithGoogle();
        } catch (err) {
            setError("Unable to sign in. Please try again.");
        } finally {
            setGoogleLoading(false);
        }
    };

    const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

    return (
        <div onClick={onBackdrop} style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'oklch(0.22 0.025 75 / 0.5)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
            animation: 'fadeIn 0.18s ease'
        }}>
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(24px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
            `}</style>
            <div style={{
                background: '#fff', borderRadius: 28, width: '100%', maxWidth: 400,
                boxShadow: '0 32px 80px oklch(0.22 0.025 75 / 0.22)',
                overflow: 'hidden',
                animation: 'slideUp 0.22s cubic-bezier(0.34,1.4,0.64,1)'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, oklch(0.93 0.08 52) 0%, oklch(0.92 0.07 148) 100%)',
                    padding: '28px 32px 20px', position: 'relative'
                }}>
                    <button onClick={onClose} style={{
                        position: 'absolute', top: 16, right: 16,
                        width: 32, height: 32, borderRadius: 99, border: 'none',
                        background: 'oklch(0.22 0.025 75 / 0.1)', cursor: 'pointer',
                        fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 16,
                        color: 'oklch(0.50 0.02 75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.15s'
                    }}>✕</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: 10, background: 'oklch(0.72 0.19 52)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 18, color: '#fff'
                        }}>Z</div>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 20, color: 'oklch(0.22 0.025 75)' }}>
                            Zeno<span style={{ color: 'oklch(0.58 0.19 52)' }}>kids </span>
                                                        {initialTab === 'login' ? 'Sign in' : 'Create Account'}

                        </span>
                    </div>
                </div>

                <div style={{ padding: '32px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <p style={{ fontSize: '14px', color: 'oklch(0.50 0.02 75)' }}>
                            Sign in with your Google account to continue.
                        </p>
                    </div>

                    <button onClick={handleGoogle} disabled={googleLoading} style={{
                        width: '100%', padding: '16px', borderRadius: 16,
                        background: '#fff', color: 'oklch(0.22 0.025 75)',
                        border: '2px solid oklch(0.88 0.02 75)',
                        cursor: googleLoading ? 'wait' : 'pointer',
                        fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                        transition: 'all 0.15s',
                        boxShadow: '0 2px 10px oklch(0.22 0.025 75 / 0.05)'
                    }}>
                        <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
                            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335" />
                        </svg>
                        {googleLoading ? 'Connecting…' : 'Continue with Google'}
                    </button>

                    {error && (
                        <p style={{ color: '#ef4444', textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
                            {error}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Floating Cards Sub-component ──────────────────────────────────────────────
function FloatingCards() {
    const cards = [
        { top: '0%', left: '5%', bg: 'oklch(0.93 0.08 52)', label: '📖 Chapter uploaded', sub: 'ಪ್ರಕೃತಿ - Class 4', delay: 0 },
        { top: '28%', left: '0%', bg: 'oklch(0.92 0.07 148)', label: '🎮 Activity ready!', sub: 'Word matching game', delay: 0.15 },
        { top: '56%', left: '8%', bg: 'oklch(0.92 0.06 240)', label: '🏆 Arjun scored', sub: '95 / 100 — New record!', delay: 0.3 }
    ];

    return (
        <div style={{ position: 'relative', height: 460 }}>
            <style>{`
                @keyframes floatCard { from { transform: translateY(0px); } to { transform: translateY(-10px); } }
            `}</style>
            <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                width: 240, height: 420, borderRadius: 36, background: '#fff',
                boxShadow: '0 24px 80px oklch(0.22 0.025 75 / 0.18)',
                overflow: 'hidden', border: '6px solid #fff'
            }}>
                <div style={{ height: 28, background: 'oklch(0.72 0.19 52)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 60, height: 6, background: 'oklch(0.72 0.19 52 / 0.4)', borderRadius: 99 }} />
                </div>
                <div style={{ padding: 16 }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 13, color: 'oklch(0.50 0.02 75)', marginBottom: 8 }}>TODAY&apos;S ACTIVITY</div>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 16, marginBottom: 12 }}>Match the Kannada words!</div>
                    {[
                        { k: 'ಮರ', e: 'Tree', matched: true },
                        { k: 'ನೀರು', e: 'Water', matched: false },
                        { k: 'ಹೂವು', e: 'Flower', matched: true }
                    ].map((pair, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                            <div style={{
                                flex: 1, padding: '8px 12px', borderRadius: 12,
                                background: pair.matched ? 'oklch(0.92 0.07 148)' : 'oklch(0.93 0.08 52)',
                                fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 18,
                                textAlign: 'center', color: pair.matched ? 'oklch(0.50 0.17 148)' : 'oklch(0.58 0.19 52)'
                            }}>{pair.k}</div>
                            <div style={{ color: 'oklch(0.50 0.02 75)', fontSize: 12 }}>→</div>
                            <div style={{
                                flex: 1, padding: '8px 12px', borderRadius: 12,
                                background: pair.matched ? 'oklch(0.92 0.07 148)' : '#f5f5f5',
                                fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: 14,
                                textAlign: 'center', color: pair.matched ? 'oklch(0.50 0.17 148)' : 'oklch(0.50 0.02 75)'
                            }}>{pair.e}</div>
                        </div>
                    ))}
                    <div style={{ marginTop: 16, padding: 12, background: 'oklch(0.93 0.08 52)', borderRadius: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 12, color: 'oklch(0.58 0.19 52)' }}>XP Today</span>
                            <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 12, color: 'oklch(0.58 0.19 52)' }}>70 / 100</span>
                        </div>
                        <div style={{ height: 8, background: 'oklch(0.72 0.19 52 / 0.2)', borderRadius: 99 }}>
                            <div style={{ width: '70%', height: '100%', background: 'oklch(0.72 0.19 52)', borderRadius: 99 }} />
                        </div>
                    </div>
                    <div style={{
                        marginTop: 16, padding: '12px', borderRadius: 14,
                        background: 'oklch(0.72 0.19 52)', color: '#fff',
                        fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 15,
                        textAlign: 'center'
                    }}>Continue →</div>
                </div>
            </div>

            {cards.map((c, i) => (
                <div key={i} style={{
                    position: 'absolute', top: c.top, left: c.left,
                    background: c.bg, borderRadius: 16, padding: '12px 16px',
                    boxShadow: '0 8px 24px oklch(0.22 0.025 75 / 0.12)',
                    minWidth: 160, zIndex: 2,
                    animation: `floatCard 3s ease-in-out ${c.delay}s infinite alternate`
                }}>
                    <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 13 }}>{c.label}</div>
                    <div style={{ fontSize: 11, color: 'oklch(0.50 0.02 75)', marginTop: 2 }}>{c.sub}</div>
                </div>
            ))}
        </div>
    );
}

const LoginPage = ({ onLogin, onSignup, onForgotPassword, onSignWithGoogle }) => {
    const [loginOpen, setLoginOpen] = useState(false);
    const [initialTab, setInitialTab] = useState('login');
    const [scrolled, setScrolled] = useState(false);

    // Contact form state
    const [contactForm, setContactForm] = useState({
        name: '',
        phone: '',
        role: 'parent',
        message: ''
    });
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [feedbackStatus, setFeedbackStatus] = useState(null); // 'success' | 'error'

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const openLogin = (tab = 'login') => {
        setInitialTab(tab);
        setLoginOpen(true);
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmittingFeedback(true);
        setFeedbackStatus(null);

        try {
            await addDoc(collection(db, 'feedback'), {
                ...contactForm,
                to: 'saritharkamath@gmail.com', // For 'Trigger Email' extension
                timestamp: serverTimestamp(),
                source: 'Landing Page Contact Form'
            });
            setFeedbackStatus('success');
            setContactForm({ name: '', phone: '', role: 'parent', message: '' });
        } catch (error) {
            setFeedbackStatus('error');
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    const links = [
        { label: 'How it works', href: '#how' },
        { label: 'Features', href: '#features' },
        // { label: 'Pricing', href: '#pricing' },
        { label: 'About', href: '#about' }
    ];

    const features = [
        { icon: '🗂️', title: 'CBSE Grades 3 to 8', desc: 'All chapters and activities from CBSE Kannada textbooks transformed into engaging digital experiences.', color: 'oklch(0.72 0.19 52)' },
        { icon: '🏅', title: 'Gamified Progress', desc: 'XP, streaks, and badges keep kids coming back. Learning feels like levelling up in a game.', color: 'oklch(0.62 0.17 295)' },
        { icon: '🎯', title: 'Personalized Syllabus', desc: 'Pick relevant chapters and follow your exact school syllabus.', color: 'oklch(0.62 0.17 240)' },
        { icon: '📄', title: 'Practice Made Easy', desc: 'Notes. Worksheets. Sample Papers. Ready to download — no more manual practice questions.', color: 'oklch(0.62 0.17 240)' },
        { icon: '📊', title: 'Progress Dashboard', desc: 'Parents get a clear view of how each child is doing, chapter by chapter.', color: 'oklch(0.62 0.17 240)' },
        { icon: '🔒', title: 'Safe for kids', desc: 'No ads, no social features, no distractions. Pure focused learning environment.', color: 'oklch(0.72 0.19 52)' }
    ];

    return (
        <div style={{ fontFamily: 'Nunito Sans, sans-serif', background: 'oklch(0.985 0.008 75)', color: 'oklch(0.22 0.025 75)', overflowX: 'hidden' }}>
            {loginOpen && (
                <LoginModal 
                    initialTab={initialTab}
                    onClose={() => setLoginOpen(false)} 
                    onSignWithGoogle={onSignWithGoogle}
                />
            )}

            {/* Navigation */}
            <nav style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
                background: scrolled ? 'rgba(253,248,242,0.95)' : 'transparent',
                backdropFilter: scrolled ? 'blur(12px)' : 'none',
                transition: 'background 0.3s, box-shadow 0.3s',
                boxShadow: scrolled ? '0 1px 24px oklch(0.22 0.025 75 / 0.08)' : 'none',
                borderBottom: scrolled ? '1px solid oklch(0.9 0.015 75)' : 'none'
            }}>
                <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <a href="#" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                        <div style={{
                            width: 38, height: 38, borderRadius: 12, background: 'oklch(0.72 0.19 52)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 20, color: '#fff',
                            boxShadow: '0 4px 12px oklch(0.93 0.08 52)'
                        }}>Z</div>
                        <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 22, color: 'oklch(0.22 0.025 75)' }}>
                            Zeno<span style={{ color: 'oklch(0.72 0.19 52)' }}>kids</span>
                        </span>
                    </a>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 28, fontSize: 15, fontWeight: 600 }}>
                        {links.map((l) => (
                            <a key={l.href} href={l.href} style={{ color: 'oklch(0.50 0.02 75)', textDecoration: 'none', transition: 'color 0.2s' }}>
                                {l.label}
                            </a>
                        ))}
                        <button onClick={() => openLogin('login')} style={{
                            background: 'oklch(0.72 0.19 52)', color: '#fff', border: 'none',
                            padding: '9px 20px', borderRadius: 99,
                            fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 15,
                            cursor: 'pointer', transition: 'all 0.15s'
                        }}>Log in</button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section style={{
                minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                padding: '120px 24px 80px', position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: -80, right: -80, width: 520, height: 520, borderRadius: '50%', background: 'oklch(0.93 0.08 52)', opacity: 0.6, filter: 'blur(60px)' }} />
                <div style={{ position: 'absolute', bottom: -60, left: -60, width: 380, height: 380, borderRadius: '50%', background: 'oklch(0.92 0.07 148)', opacity: 0.5, filter: 'blur(50px)' }} />
                <div style={{ maxWidth: 1140, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
                    <div>
                        <div style={{ ...S.chip, background: 'oklch(0.93 0.08 52)', color: 'oklch(0.58 0.19 52)' }}>🇮🇳 Made for Indian classrooms</div>
                        <h1 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(38px, 5.5vw, 68px)', lineHeight: 1.08, marginBottom: 24 }}>
                            Make learning Kannada fun for your child
                        </h1>
                        <p style={{ ...S.lead, marginBottom: 40 }}>
                            For CBSE students who don’t speak Kannada at home. Learn chapters through games, visuals, and practice — not memorization.
                        </p>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                            <button onClick={() => openLogin('signup')} style={{
                                background: 'oklch(0.72 0.19 52)', color: '#fff', border: 'none',
                                padding: '16px 36px', borderRadius: 99,
                                fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 18,
                                cursor: 'pointer', boxShadow: '0 6px 24px oklch(0.72 0.19 52 / 0.35)'
                            }}>Try it free →</button>
                            <a href="#how" style={{
                                color: 'oklch(0.22 0.025 75)', fontWeight: 700, fontSize: 16,
                                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '16px 28px', borderRadius: 99, border: '2px solid oklch(0.85 0.02 75)'
                            }}>▶ Watch demo</a>
                        </div>
                    </div>
                    <div><FloatingCards /></div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" style={{ ...S.section }}>
                <div style={{ textAlign: 'center', marginBottom: 64 }}>
                    <div style={{ ...S.chip, background: 'oklch(0.92 0.07 148)', color: 'oklch(0.50 0.17 148)' }}>Features</div>
                    <h2 style={{ ...S.h2 }}>Everything kids need to love learning.</h2>
                    <p style={{ ...S.lead, margin: '0 auto' }}>Built for school syllabus practice and exam confidence.</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                    {features.map((f, i) => (
                        <div key={i} style={{ ...S.card, border: '2px solid oklch(0.93 0.01 75)' }}>
                            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 19, marginBottom: 12 }}>{f.icon} {f.title}</h3>
                            <p style={{ color: 'oklch(0.50 0.02 75)', lineHeight: 1.6, fontSize: 14 }}>{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Testimonials Section */}
            <section style={{
                background: 'linear-gradient(180deg, oklch(0.72 0.19 52) 0%, oklch(0.58 0.19 52) 100%)',
                padding: '96px 24px', position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: -120, right: -100, width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
                <div style={{ position: 'absolute', bottom: -140, left: -120, width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.10)' }} />
                <div style={{ maxWidth: 1140, margin: '0 auto', position: 'relative' }}>
                    <div style={{ textAlign: 'center', marginBottom: 56 }}>
                        <div style={{ ...S.chip, background: 'rgba(255,255,255,0.22)', color: '#fff' }}>What parents say</div>
                        <h2 style={{ ...S.h2, color: '#fff' }}>Kids asked for more.</h2>
                        <p style={{ ...S.lead, color: 'rgba(255,255,255,0.86)', margin: '0 auto' }}>That&apos;s the best feedback we&apos;ve ever received.</p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                        {[
                            { text: "Ira used to dread learning Kannada. Now she asks me if she can play Zenokids before dinner!", author: "Vaishali", role: "Parent, Bengaluru", emoji: "👩" },
                            { text: "Arjun hated tuitions. Now he says Zenokids is better than both tuition and school. I couldn&apos;t be happier.", author: "Suma B.", role: "Parent, Mysuru", emoji: "👩‍🏫" },
                            { text: "Kannada was becoming a daily struggle for my child. It used to stress him out. Zenokids made it simple and enjoyable.", author: "Rajesh K.", role: "Parent, Bengaluru", emoji: "👨" }
                        ].map((q, i) => (
                            <div key={i} style={{
                                background: '#fff', borderRadius: 28, padding: 34,
                                boxShadow: '0 16px 42px rgba(82, 38, 0, 0.16)',
                                display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 260
                            }}>
                                <div>
                                    <div style={{ width: 46, height: 46, borderRadius: 14, background: 'oklch(0.93 0.08 52)', color: 'oklch(0.58 0.19 52)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 900, marginBottom: 22 }}>“</div>
                                    <p style={{ color: 'oklch(0.22 0.025 75)', lineHeight: 1.7, fontSize: 16, marginBottom: 28, fontStyle: 'italic', fontWeight: 600 }}>{q.text}</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg, #fff0d9, #ffd2a1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 21 }}>{q.emoji}</div>
                                    <div>
                                        <div style={{ color: 'oklch(0.22 0.025 75)', fontWeight: 800, fontSize: 15 }}>{q.author}</div>
                                        <div style={{ color: 'oklch(0.50 0.02 75)', fontSize: 13 }}>{q.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            {/* <section id="pricing" style={{ ...S.section }}>
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <div style={{ ...S.chip, background: 'oklch(0.92 0.07 148)', color: 'oklch(0.50 0.17 148)' }}>Pricing</div>
                    <h2 style={{ ...S.h2 }}>Simple, honest pricing</h2>
                    <p style={{ ...S.lead, margin: '0 auto 32px' }}>Start free. Upgrade when you&apos;re ready.</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                    {[
                        { name: 'Free', price: '0', desc: 'Perfect to get started', features: ['2 chapters', 'All activity types', 'Basic tracking'] },
                        { name: 'Family', price: '499', desc: 'Best for home learners', popular: true, features: ['Unlimited chapters', 'All activities', 'Dashboard', 'Priority support'] },
                        { name: 'School', price: 'Custom', desc: 'For tuitions & schools', features: ['Teacher dashboard', 'Bulk accounts', 'Onboarding'] }
                    ].map((plan, i) => (
                        <div key={i} style={{ ...S.card, border: plan.popular ? '2px solid oklch(0.72 0.19 52)' : '2px solid oklch(0.92 0.01 75)', position: 'relative' }}>
                            {plan.popular && <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'oklch(0.72 0.19 52)', color: '#fff', padding: '4px 16px', borderRadius: 99, fontSize: 12, fontWeight: 800 }}>Most popular</div>}
                            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{plan.name}</div>
                            <div style={{ color: 'oklch(0.50 0.02 75)', fontSize: 14, marginBottom: 20 }}>{plan.desc}</div>
                            <div style={{ fontSize: 36, fontWeight: 900, marginBottom: 24 }}>{plan.price === 'Custom' ? 'Custom' : `₹${plan.price}`}{plan.price !== '0' && plan.price !== 'Custom' && <span style={{ fontSize: 14, fontWeight: 600 }}>/mo</span>}</div>
                            <div style={{ flex: 1, marginBottom: 24 }}>
                                {plan.features.map((f, j) => (
                                    <div key={j} style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 14, color: 'oklch(0.50 0.02 75)' }}>
                                        <span style={{ color: 'oklch(0.65 0.17 148)', fontWeight: 800 }}>✓</span> {f}
                                    </div>
                                ))}
                            </div>
                            <button onClick={() => openLogin('signup')} style={{ width: '100%', padding: '14px', borderRadius: 14, background: plan.popular ? 'oklch(0.72 0.19 52)' : 'oklch(0.88 0.02 75)', color: plan.popular ? '#fff' : 'oklch(0.22 0.025 75)', border: 'none', fontWeight: 800, cursor: 'pointer' }}>Get started</button>
                        </div>
                    ))}
                </div>
            </section> */}

            {/* About Section */}
            <section id="about" style={{ background: 'oklch(0.97 0.012 75)', padding: '96px 24px' }}>
                <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
                    <div style={{
                        height: 360, borderRadius: 28, background: 'oklch(0.93 0.08 52)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        border: '3px dashed oklch(0.72 0.19 52)', gap: 12
                    }}>
                        <div style={{ fontSize: 56 }}>🤝</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'oklch(0.58 0.19 52)', textAlign: 'center', padding: '0 24px' }}>founder photo / team shot</div>
                    </div>
                    <div>
                        <div style={{ ...S.chip, background: 'oklch(0.93 0.08 52)', color: 'oklch(0.58 0.19 52)' }}>Our story</div>
                        <h2 style={{ ...S.h2 }}>Built by a mom who cares</h2>
                        <p style={{ color: 'oklch(0.50 0.02 75)', lineHeight: 1.75, marginBottom: 16, fontSize: 15 }}>
                            Zenokids was born from a simple frustration: regional language learning in Indian schools is stuck in the past. Textbooks are rich with culture and stories — but the way kids are taught makes them dread the subject.
                        </p>
                        <p style={{ color: 'oklch(0.50 0.02 75)', lineHeight: 1.75, marginBottom: 16, fontSize: 15 }}>
                            We built a tool that takes those same textbook chapters and turns them into something kids can&apos;t stop playing. The results speak for themselves — kids are asking for <em>more</em>.
                        </p>
                        <p style={{ color: 'oklch(0.50 0.02 75)', lineHeight: 1.75, fontSize: 15 }}>
                            We started with CBSE Kannada and are building towards other major Indian regional languages and boards.
                        </p>
                    </div>
                </div>
            </section>

            {/* Blog Section */}
            <section id="blog" style={{ ...S.section }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48 }}>
                    <div>
                        <div style={{ ...S.chip, background: 'oklch(0.92 0.06 240)', color: 'oklch(0.62 0.17 240)' }}>Blog</div>
                        <h2 style={{ ...S.h2, marginBottom: 0 }}>From the Zenokids team</h2>
                    </div>
                    <a href="#" style={{ color: 'oklch(0.72 0.19 52)', fontWeight: 700, textDecoration: 'none', fontSize: 15 }}>All posts →</a>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                    {[
                        { emoji: '🎮', tag: 'Pedagogy', title: 'Why gamification works for language learning', excerpt: 'When kids earn XP for vocabulary, they practice 3× more. Here&apos;s the science behind why games and language learning are a perfect match.', date: 'April 2026' },
                        { emoji: '📚', tag: 'Kannada', title: 'The state of Kannada education in Karnataka schools', excerpt: 'A look at the challenges teachers and students face with regional language instruction today, and how technology can help bridge the gap.', date: 'March 2026' },
                        { emoji: '🏆', tag: 'Story', title: 'From dreading homework to 90 in the exam', excerpt: 'Arjun used to avoid Kannada class. Three weeks with Zenokids later, his teacher pulled his mother aside to share something remarkable.', date: 'March 2026' }
                    ].map((p, i) => (
                        <div key={i} style={{ ...S.card, cursor: 'pointer', transition: 'transform 0.2s' }}>
                            <div style={{ fontSize: 40, marginBottom: 16 }}>{p.emoji}</div>
                            <div style={{ ...S.chip, background: 'oklch(0.92 0.06 240)', color: 'oklch(0.62 0.17 240)', marginBottom: 12 }}>{p.tag}</div>
                            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 18, marginBottom: 10, lineHeight: 1.3 }}>{p.title}</h3>
                            <p style={{ color: 'oklch(0.50 0.02 75)', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>{p.excerpt}</p>
                            <div style={{ fontSize: 12, color: 'oklch(0.50 0.02 75)', fontWeight: 600 }}>{p.date}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" style={{ background: 'oklch(0.97 0.012 75)', padding: '96px 24px' }}>
                <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
                    <h2 style={{ ...S.h2 }}>We would love to hear from you</h2>
                    
                    {feedbackStatus === 'success' ? (
                        <div style={{ ...S.card, textAlign: 'center', padding: '48px' }}>
                            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
                            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '24px', marginBottom: '8px' }}>Thank you!</h3>
                            <p style={{ color: 'oklch(0.50 0.02 75)' }}>Your feedback has been sent. We&apos;ll get back to you soon!</p>
                            <button 
                                onClick={() => setFeedbackStatus(null)}
                                style={{
                                    marginTop: '24px', padding: '12px 24px', borderRadius: '12px',
                                    background: 'oklch(0.72 0.19 52)', color: '#fff', border: 'none',
                                    fontWeight: 800, cursor: 'pointer'
                                }}
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleContactSubmit} style={{ ...S.card, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div>
                                    <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>Your name</label>
                                    <input 
                                        required
                                        value={contactForm.name}
                                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                                        placeholder="Priya Sharma" 
                                        style={modalInputStyle} 
                                    />
                                </div>
                                <div>
                                    <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>Phone number</label>
                                    <input 
                                        required
                                        type="tel" 
                                        value={contactForm.phone}
                                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                                        placeholder="+91 98765 43210" 
                                        style={modalInputStyle} 
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>I am a…</label>
                                <select 
                                    value={contactForm.role}
                                    onChange={(e) => setContactForm({...contactForm, role: e.target.value})}
                                    style={{ ...modalInputStyle, appearance: 'none', cursor: 'pointer' }}
                                >
                                    <option value="parent">Parent</option>
                                    <option value="teacher">Teacher</option>
                                    <option value="school">School administrator</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>Message <span style={{ color: 'oklch(0.50 0.02 75)', fontWeight: 400 }}>(optional)</span></label>
                                <textarea 
                                    value={contactForm.message}
                                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                                    placeholder="Tell us about your class, school, or kids..." 
                                    rows={4} 
                                    style={{ ...modalInputStyle, resize: 'vertical', minHeight: 100 }} 
                                />
                            </div>
                            
                            {feedbackStatus === 'error' && (
                                <p style={{ color: '#ef4444', textAlign: 'center', fontSize: '14px' }}>
                                    Something went wrong. Please try again or email us directly.
                                </p>
                            )}

                            <button 
                                type="submit" 
                                disabled={isSubmittingFeedback}
                                style={{
                                    padding: '16px', borderRadius: 14, background: isSubmittingFeedback ? 'oklch(0.72 0.19 52 / 0.7)' : 'oklch(0.72 0.19 52)', 
                                    color: '#fff', border: 'none', cursor: isSubmittingFeedback ? 'wait' : 'pointer', 
                                    fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 18,
                                    transition: 'all 0.2s'
                                }}
                            >
                                {isSubmittingFeedback ? 'Sending...' : 'Submit →'}
                            </button>
                        </form>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer style={{ background: 'oklch(0.22 0.025 75)', color: '#fff', padding: '64px 24px 32px' }}>
                <div style={{ maxWidth: 1140, margin: '0 auto', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'oklch(0.72 0.19 52)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 18 }}>Z</div>
                        <span style={{ fontWeight: 900, fontSize: 22 }}>Zenokids</span>
                    </div>
                    <p style={{ opacity: 0.5, fontSize: 14, marginBottom: 32 }}>Making regional language learning joyful for every Indian child.</p>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 24, fontSize: 13, opacity: 0.4 }}>
                        © 2026 Zenokids. Made with ❤️ in India.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LoginPage;

